import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './AboutUs.css';

gsap.registerPlugin(ScrollTrigger);

const AboutUs = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const titleRef = useRef(null);
  const para1Ref = useRef(null);
  const para2Ref = useRef(null);
  const para3Ref = useRef(null);
  const btnRef = useRef(null);

  useGSAP(() => {
    const tl = gsap.timeline({ paused: true });

    ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top 80%',       // animate IN when section top reaches 80% of viewport
      end: 'bottom 10%',    // animate OUT when section bottom passes 10% of viewport
      onEnter: () => tl.play(),     // scrolling down into section → play forward
      onLeave: () => tl.reverse(),  // scrolling down past section → reverse out
      onEnterBack: () => tl.play(),     // scrolling back up into section → play forward
      onLeaveBack: () => tl.reverse(),  // scrolling back above section → reverse out
    });

    // 1. Image slides in from the left
    tl.fromTo(
      imageRef.current,
      { x: -120, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    // 2. Title words cascade up (each word is already wrapped in .mask-word spans)
    tl.fromTo(
      titleRef.current.querySelectorAll('.mask-word'),
      { y: '110%', opacity: 0 },
      { y: '0%', opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.04 },
      '-=0.3'   // overlap slightly with image animation
    );

    // 3. Paragraphs fade up one by one
    tl.fromTo(
      [para1Ref.current, para2Ref.current, para3Ref.current],
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.55, ease: 'power2.out', stagger: 0.18 },
      '-=0.2'
    );

    // 4. CTA button pops in last
    tl.fromTo(
      btnRef.current,
      { y: 20, opacity: 0, scale: 0.92 },
      { y: 0, opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(1.4)' },
      '-=0.1'
    );
  }, { scope: sectionRef });

  const titleText = "WELCOME TO THE TECHNO INDIA MANCHESTER CITY FOOTBALL SCHOOL!".split(" ");

  return (
    <section id="about" className="about-section" ref={sectionRef}>
      <div className="about-container">

        {/* Image — slides in from left */}
        <div className="about-image" ref={imageRef}>
          <img src="/about-us.webp" alt="Football School Students" loading="lazy" width="800" height="600" />
        </div>

        <div className="about-content">
          {/* Title words — cascade up */}
          <h1
            className="masked-title"
            ref={titleRef}
            style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25em', marginBottom: '1rem' }}
          >
            {titleText.map((word, index) => (
              <span
                key={index}
                className="mask-word-wrap"
                style={{ overflow: 'hidden', display: 'inline-block', paddingBottom: '0.1em' }}
              >
                <span className="mask-word" style={{ display: 'inline-block' }}>
                  {word}
                </span>
              </span>
            ))}
          </h1>

          {/* Paragraphs — fade up sequentially */}
          <p ref={para1Ref}>
            Where passion meets professional excellence. In collaboration with the renowned <strong>City Football Group</strong>, our Kolkata academy is set to revolutionize football in India for children aged 3 to 17 years.
          </p>
          <p ref={para2Ref}>
            Equipped with state-of-the-art facilities, the school is led by certified <strong>Manchester City coaches</strong>, offering world-class training and international expertise. Exceptional players may even have the opportunity to train at Manchester City's UK facilities for greater exposure and development.
          </p>
          <p ref={para3Ref}>
            Progress is tracked with comprehensive performance reports and personalised improvement plans, directly generated by <strong>Manchester City Football School</strong> in the UK. Leveraging advanced sports technology, these reports refine skills, enhance tactical understanding, and shape the future of aspiring footballers.
          </p>

          {/* CTA — pops in last */}
          <Link to="/faq" className="read-more-btn" ref={btnRef} aria-label="Explore our frequently asked questions">
            <span>Explore FAQs</span>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default AboutUs;
